/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  afterRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChildren
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { IAttributeListItem } from '../../../core/models';
import { CustomTooltipDirective } from '../../../shared';

const MAX_DISPLAY_LENGTH = 12;

export interface AttributeChip {
  attributeId: string;
  attributeName: string;
  value: string;
  displayValue: string;
  tooltip: string;
}

@Component({
  selector: 'fm-attribute-chips',
  standalone: true,
  imports: [MatIconModule, MatTooltipModule, CustomTooltipDirective],
  templateUrl: './attribute-chips.component.html',
  styleUrl: './attribute-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.display]': 'chips().length === 0 ? "none" : "block"'
  }
})
export class AttributeChipsComponent {
  private _needsMeasurementGuard = false;

  readonly selectedAttributes = input<IAttributeListItem[]>([]);
  readonly showSkeleton = input(false);
  readonly chipRemoved = output<{ attributeId: string; value: string }>();
  readonly overflowClicked = output<void>();

  /**
   * Maps selected attributes to components chips
   */
  readonly chips = computed(() => {
    const items = this.selectedAttributes();
    const result: AttributeChip[] = [];
    for (const item of items) {
      for (const value of item.stringValues) {
        result.push({
          attributeId: item.attributeId,
          attributeName: item.name,
          value,
          displayValue: value.length > MAX_DISPLAY_LENGTH ? `${value.substring(0, MAX_DISPLAY_LENGTH)}…` : value,
          tooltip: `${item.name}: ${value}`
        });
      }
    }
    return result;
  });

  /** Query all ghost chip elements to know when the DOM is ready for measurement. */
  readonly ghostChipElements = viewChildren('ghostChip', { read: ElementRef });

  readonly visibleCount = signal(0);

  constructor() {
    // When ghost chips change, flag for measurement
    effect(() => {
      this.ghostChipElements();
      this._needsMeasurementGuard = true;
    });

    // After Angular renders the DOM, measure chip positions
    afterRender({
      read: () => {
        if (!this._needsMeasurementGuard) {
          return;
        }
        this._needsMeasurementGuard = false;

        const elements = this.ghostChipElements();
        if (elements.length === 0) {
          this.visibleCount.set(0);
          return;
        }

        this.visibleCount.set(this._calculateLayout(elements));
      }
    });
  }

  readonly visibleChips = computed(() => {
    const all = this.chips();
    const count = this.visibleCount();
    return count < all.length ? all.slice(0, count) : all;
  });

  readonly overflowCount = computed(() => {
    const total = this.chips().length;
    const visible = this.visibleCount();
    return Math.max(0, total - visible);
  });

  onRemove(chip: AttributeChip): void {
    this.chipRemoved.emit({ attributeId: chip.attributeId, value: chip.value });
  }

  trackByChip(_: number, chip: AttributeChip): string {
    return `${chip.attributeId}:${chip.value}`;
  }

  private _calculateLayout(elements: readonly ElementRef[]): number {
    const OVERFLOW_BUTTON_WIDTH = 75;
    const GAP = 8;
    const firstTop = elements[0]?.nativeElement.offsetTop;
    let secondRowTop: number | null = null;

    for (let i = 0; i < elements.length; i++) {
      const top = elements[i].nativeElement.offsetTop;

      if (top > firstTop && secondRowTop === null) {
        secondRowTop = top;
      }

      if (secondRowTop !== null && top > secondRowTop) {
        // Third row reached — reserve space for the "+X more" button
        const containerWidth = elements[0].nativeElement.parentElement.offsetWidth;
        const lastChip = elements[i - 1].nativeElement;
        const lastChipRight = lastChip.offsetLeft + lastChip.offsetWidth;
        const spaceLeft = containerWidth - lastChipRight;

        if (spaceLeft < OVERFLOW_BUTTON_WIDTH + GAP) {
          return Math.max(1, i - 1);
        }
        return i;
      }
    }

    return elements.length;
  }
}
